import { groupBy } from 'lodash-es'
import { nanoid } from 'nanoid'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import {
  type AlignDimensionEntity,
  type AngularDimensionEntity,
  ArcEntity,
  type BaseEntity,
  CircleEntity,
  type EllipseEntity,
  type HatchEntity,
  type IEntity,
  type IPolylineVertice,
  type MLeaderEntity,
  type MTextEntity,
  PolylineEntity,
  type RayEntity,
  SplineEntity,
} from '@/plugins/cad/module/CADModule/model/entity'
import { SplineUtils } from '@/plugins/cad/module/utils/SplineUtils'
import { getLoggerManager } from '@/shared/logging'
import { BreakUtils } from './utils/breakUtils'
import type { ExtendEndType } from './utils/extendUtils'
import { OffsetUtils } from './utils/offsetUtils'
import { TrimUtils } from './utils/trimUtils'

/**
 * 实体移动参数。
 */
export interface MoveParams {
  /** 平移向量，单位与实体坐标一致 */
  translate: { x: number; y: number }
}

/**
 * 实体复制参数。
 */
export interface CopyParams {
  /** 复制后相对原实体的平移向量 */
  translate: { x: number; y: number }
}

/**
 * 实体旋转参数。
 */
export interface RotateParams {
  /** 旋转中心 */
  center: { x: number; y: number }
  /** 旋转角度，单位弧度 */
  angle: number
}

/** 偏移侧：1 表示左侧，-1 表示右侧（沿实体方向定义） */
export type OffsetSide = 1 | -1

/**
 * 实体偏移参数。
 */
export interface OffsetParams {
  /** 偏移距离，单位米 */
  distance: number
  /** 偏移方向 */
  side: OffsetSide
}

/**
 * 实体打断参数。
 */
export interface BreakParams {
  /** 打断起点 */
  firstPoint: { x: number; y: number }
  /** 打断终点 */
  secondPoint: { x: number; y: number }
}

/**
 * 打断/修剪的执行结果。
 */
export interface BreakResult {
  /** 操作后新增的实体 */
  createdEntities: IEntity[]
  /** 操作后需删除的实体 */
  deleteEntities: IEntity[]
}

/**
 * 实体修剪参数。
 */
export interface TrimParams {
  /** 鼠标所在点，用于确定要剪除的片段 */
  cursorPoint: { x: number; y: number }
  /** 参与求交的边界实体列表 */
  boundaries: IEntity[]
}

/**
 * 实体延伸参数。
 */
export interface ExtendEntityParams {
  /** 要延伸的端点（起点或终点） */
  endType: ExtendEndType
  /** 延伸后端点的新位置 */
  newPoint: { x: number; y: number }
}

export type { ExtendEndType } from './utils/extendUtils'

const log = getLoggerManager().getLogger('cad')

/**
 * 实体编辑入口：提供移动、复制、旋转、偏移、打断、修剪、延伸等静态方法。
 */
export class EntityEditHandler {
  /**
   * 平移实体。
   *
   * @param entities - 要平移的实体集合
   * @param params - 平移参数
   */
  static move(entities: IEntity[], params: MoveParams) {
    entities.forEach((entity) => {
      EntityEditHandler.moveEntity(entity, params)
    })
  }

  /**
   * 复制实体并在副本上应用平移。
   *
   * @param entities - 源实体集合
   * @param params - 复制参数
   * @returns 复制得到的新实体集合，id 已重新分配
   */
  static copy(entities: IEntity[], params: CopyParams): IEntity[] {
    const copyEntities: IEntity[] = []
    entities.forEach((entity) => {
      const copyEntity = (entity as BaseEntity).clone()
      copyEntity.id = nanoid()
      copyEntities.push(copyEntity)
      EntityEditHandler.moveEntity(copyEntity, params)
    })
    return copyEntities
  }

  /**
   * 旋转实体集合。
   *
   * @param entities - 要旋转的实体集合
   * @param params - 旋转参数
   */
  static rotate(entities: IEntity[], params: RotateParams) {
    entities.forEach((entity) => {
      EntityEditHandler.rotateEntity(entity, params)
    })
  }

  /**
   * 缩放实体
   * @param entities 要缩放的实体数组
   * @param scale 缩放比例(必须大于0)
   */
  static scale(entities: IEntity[], scale: number) {
    if (scale === 1 || scale <= 0) return
    entities.forEach((entity) => {
      EntityEditHandler.scaleEntity(entity, scale)
    })
  }

  /**
   * 偏移实体。
   *
   * @param entity - 源实体
   * @param params - 偏移参数
   * @returns 偏移后的新实体；不支持或偏移无效时返回 null
   */
  static offset(entity: IEntity, params: OffsetParams): IEntity | null {
    return OffsetUtils.offsetEntity(entity, params)
  }

  /**
   * 打断实体集合。
   *
   * @param entities - 要打断的实体集合
   * @param params - 打断参数
   * @returns 新增与需删除的实体
   */
  static break(entities: IEntity[], params: BreakParams): BreakResult {
    const result = {
      createdEntities: [] as IEntity[],
      deleteEntities: [] as IEntity[],
    }
    for (const entity of entities) {
      const { createdEntities, deleteEntities } = BreakUtils.breakEntity(entity, params)
      result.createdEntities.push(...createdEntities)
      result.deleteEntities.push(...deleteEntities)
    }
    return result
  }

  /**
   * 修剪实体。
   *
   * @param entity - 要修剪的实体
   * @param params - 修剪参数
   * @returns 新增与需删除的实体
   */
  static trim(entity: IEntity, params: TrimParams): BreakResult {
    return TrimUtils.trimEntity(entity, params.cursorPoint, params.boundaries)
  }

  /**
   * 延伸实体端点到新位置。
   *
   * @param entity - 要延伸的实体
   * @param params - 延伸参数，指定端点类型与新位置
   */
  static extend(entity: IEntity, params: ExtendEntityParams): void {
    const { endType, newPoint } = params
    switch (entity.type) {
      case EntityType.Polyline: {
        const poly = entity as PolylineEntity
        const v = poly.vertices
        if (endType === 'end') {
          v[v.length - 1] = { ...v[v.length - 1], x: newPoint.x, y: newPoint.y }
        } else {
          v[0] = { ...v[0], x: newPoint.x, y: newPoint.y }
        }
        break
      }
      case EntityType.Arc: {
        const arc = entity as ArcEntity
        const newAngle = Math.atan2(newPoint.y - arc.center.y, newPoint.x - arc.center.x)
        if (endType === 'end') {
          arc.endAngle = newAngle
        } else {
          arc.startAngle = newAngle
        }
        break
      }
      case EntityType.Ray: {
        if (endType === 'start') {
          const ray = entity as RayEntity
          ray.startPoint = { x: newPoint.x, y: newPoint.y }
        }
        break
      }
      case EntityType.Spline: {
        const spline = entity as SplineEntity
        const { knots, degree, controlPoints } = spline
        if (controlPoints.length < degree + 1) {
          break
        }
        const uMin = knots[degree] ?? 0
        const uMax = (knots[knots.length - degree - 1] ?? 1) - 1e-9
        const NUM_SAMPLES = 32
        const pts: { x: number; y: number }[] = []
        for (let i = 0; i <= NUM_SAMPLES; i++) {
          const u = uMin + (uMax - uMin) * (i / NUM_SAMPLES)
          const pt = SplineUtils.evaluateBSpline(u, controlPoints, knots, degree)
          pts.push({ x: pt.x, y: pt.y })
        }
        if (endType === 'end') {
          pts.push({ x: newPoint.x, y: newPoint.y })
        } else {
          pts.unshift({ x: newPoint.x, y: newPoint.y })
        }
        try {
          const fitted = SplineUtils.fitPointsToSpline(pts)
          spline.controlPoints = fitted.controlPoints
          spline.knots = fitted.knots
          spline.degree = fitted.degree
          if (spline.fitPoints?.length) {
            if (endType === 'end') {
              spline.fitPoints.push(newPoint)
            } else {
              spline.fitPoints.unshift(newPoint)
            }
          }
        } catch {
          log.warn('Spline extend: re-fit failed')
        }
        break
      }
      default:
        log.warn(`Extend operation not implemented for entity type: ${entity.type}`)
    }
  }

  /**
   * 合并实体
   * 将首尾相连的实体合并为一个实体
   * 支持混合类型实体，自动按类型分组后分别合并
   * @param entities 要合并的实体数组
   * @param tolerance 端点容差（默认 0.001）
   * @returns 合并后的实体数组
   */
  static join(
    entities: IEntity[],
    tolerance: number = 0.001
  ): {
    nonJoinedEntities: IEntity[]
    createdEntities: IEntity[]
  } {
    const result = {
      // 未合并的实体
      nonJoinedEntities: [] as IEntity[],
      // 新生成的实体
      createdEntities: [] as IEntity[],
    }
    if (entities.length < 2) {
      result.nonJoinedEntities.push(...entities)
      return result
    }

    // 只对开放的实体进行合并，闭合的实体不参与合并
    const { closedEntities, openEntities } = entities.reduce(
      (data, entity) => {
        if (
          [EntityType.Polyline, EntityType.Spline].includes(entity.type) &&
          (entity as PolylineEntity | SplineEntity).isClosed
        ) {
          data.closedEntities.push(entity)
        } else {
          data.openEntities.push(entity)
        }
        return data
      },
      {
        closedEntities: [] as IEntity[],
        openEntities: [] as IEntity[],
      }
    )
    result.nonJoinedEntities.push(...closedEntities)

    // 按类型分组
    const groupedByType: Record<EntityType, IEntity[]> = groupBy(openEntities, 'type')
    // 对每组实体分别执行合并
    for (const [type, group] of Object.entries(groupedByType)) {
      if (group.length < 2) {
        // 单个实体无需合并，直接保留
        result.nonJoinedEntities.push(...group)
        continue
      }
      // 根据类型执行合并
      const joinResult = EntityEditHandler.joinByType(type as EntityType, group, tolerance)
      result.nonJoinedEntities.push(...joinResult.nonJoined)
      result.createdEntities.push(...joinResult.created)
    }

    return result
  }

  // 根据实体类型执行不同的缩放逻辑，以原点 (0,0) 为基准
  private static scaleEntity(entity: IEntity, scale: number) {
    if (scale === 1 || scale <= 0) return
    const scalePoint = (point: IPoint) => {
      point.x *= scale
      point.y *= scale
    }
    switch (entity.type) {
      case EntityType.Polyline: {
        const { vertices } = entity as PolylineEntity
        vertices.forEach(scalePoint)
        break
      }
      case EntityType.Arc: {
        const arc = entity as ArcEntity
        scalePoint(arc.center)
        arc.radius *= scale
        break
      }
      case EntityType.Circle: {
        const circle = entity as CircleEntity
        scalePoint(circle.center)
        circle.radius *= scale
        break
      }
      case EntityType.Ellipse: {
        const ellipse = entity as EllipseEntity
        scalePoint(ellipse.center)
        scalePoint(ellipse.majorAxisEndPoint)
        break
      }
      case EntityType.Ray: {
        const ray = entity as RayEntity
        scalePoint(ray.startPoint)
        break
      }
      case EntityType.Spline: {
        const { fitPoints, controlPoints } = entity as SplineEntity
        controlPoints.forEach(scalePoint)
        fitPoints?.forEach(scalePoint)
        break
      }
      case EntityType.MText: {
        const mtext = entity as MTextEntity
        scalePoint(mtext.position)
        mtext.textHeight *= scale
        mtext.rectWidth && (mtext.rectWidth *= scale)
        mtext.rectHeight && (mtext.rectHeight *= scale)
        break
      }
      case EntityType.AlignDimension: {
        const dim = entity as AlignDimensionEntity
        ;[dim.p1, dim.p2, dim.offsetPoint, dim.textPosition].forEach(scalePoint)
        dim.textHeight *= scale
        break
      }
      case EntityType.MLeader: {
        const mLeader = entity as MLeaderEntity
        EntityEditHandler.forEachUniqueMLeaderPoint(mLeader, scalePoint)
        mLeader.textHeight *= scale
        if (typeof mLeader.landingGap === 'number') {
          mLeader.landingGap *= scale
        }
        break
      }
      case EntityType.AngularDimension: {
        const angularDimension = entity as AngularDimensionEntity
        ;[
          angularDimension.line1.startPoint,
          angularDimension.line1.endPoint,
          angularDimension.line2.startPoint,
          angularDimension.line2.endPoint,
          angularDimension.arcPoint,
          angularDimension.textPosition,
        ].forEach(scalePoint)
        angularDimension.textHeight *= scale
        break
      }
      case EntityType.Hatch: {
        const hatch = entity as HatchEntity
        if (hatch.startPoint) {
          scalePoint(hatch.startPoint)
        }
        if (typeof hatch.patternScale === 'number') {
          hatch.patternScale *= scale
        }
        EntityEditHandler.forEachHatchBoundaryEntity(hatch, (boundaryEntity) => {
          EntityEditHandler.scaleEntity(boundaryEntity, scale)
        })
        break
      }
      default:
        log.warn(`Scale operation not implemented for entity type: ${entity.type}`)
    }
  }

  // 根据实体类型执行不同的移动逻辑
  private static moveEntity(entity: IEntity, params: MoveParams) {
    const { translate } = params
    const translatePoint = (point: IPoint) => {
      point.x += translate.x
      point.y += translate.y
    }
    switch (entity.type) {
      case EntityType.Polyline: {
        const { vertices } = entity as PolylineEntity
        vertices.forEach(translatePoint)
        break
      }
      case EntityType.Arc:
      case EntityType.Circle:
      case EntityType.Ellipse: {
        const { center } = entity as ArcEntity | CircleEntity | EllipseEntity
        translatePoint(center)
        break
      }
      case EntityType.Spline: {
        const { fitPoints, controlPoints } = entity as SplineEntity
        controlPoints.forEach(translatePoint)
        fitPoints?.forEach(translatePoint)
        break
      }
      case EntityType.MText: {
        const { position } = entity as MTextEntity
        translatePoint(position)
        break
      }
      case EntityType.AlignDimension: {
        const { p1, p2, offsetPoint, textPosition } = entity as AlignDimensionEntity
        ;[p1, p2, offsetPoint, textPosition].forEach(translatePoint)
        break
      }
      case EntityType.MLeader: {
        const mLeader = entity as MLeaderEntity
        EntityEditHandler.forEachUniqueMLeaderPoint(mLeader, translatePoint)
        break
      }
      case EntityType.Ray: {
        const { startPoint } = entity as RayEntity
        translatePoint(startPoint)
        break
      }
      case EntityType.AngularDimension: {
        const angularDimension = entity as AngularDimensionEntity
        ;[
          angularDimension.line1.startPoint,
          angularDimension.line1.endPoint,
          angularDimension.line2.startPoint,
          angularDimension.line2.endPoint,
          angularDimension.arcPoint,
          angularDimension.textPosition,
        ].forEach(translatePoint)
        break
      }
      case EntityType.Hatch: {
        const hatch = entity as HatchEntity
        if (hatch.startPoint) {
          translatePoint(hatch.startPoint)
        }
        EntityEditHandler.forEachHatchBoundaryEntity(hatch, (boundaryEntity) => {
          EntityEditHandler.moveEntity(boundaryEntity, params)
        })
        break
      }
      // 其他实体类型的处理逻辑可以在这里添加
      default:
        log.warn(`Move operation not implemented for entity type: ${entity.type}`)
    }
  }

  private static rotateEntity(entity: IEntity, params: RotateParams): void {
    const { center, angle } = params
    const rotatePoint = (point: IPoint) => {
      const dx = point.x - center.x
      const dy = point.y - center.y
      point.x = center.x + dx * Math.cos(angle) - dy * Math.sin(angle)
      point.y = center.y + dx * Math.sin(angle) + dy * Math.cos(angle)
    }
    const rotateVector = (point: IPoint) => {
      const x = point.x
      const y = point.y
      point.x = x * Math.cos(angle) - y * Math.sin(angle)
      point.y = x * Math.sin(angle) + y * Math.cos(angle)
    }
    switch (entity.type) {
      case EntityType.Polyline:
        ;(entity as PolylineEntity).vertices.forEach((v) => rotatePoint(v))
        break
      case EntityType.Arc:
      case EntityType.Circle:
        rotatePoint((entity as ArcEntity | CircleEntity).center)
        if (entity.type === EntityType.Arc) {
          const arc = entity as ArcEntity
          arc.startAngle += angle
          arc.endAngle += angle
        }
        break
      case EntityType.Ellipse: {
        const ellipse = entity as EllipseEntity
        rotatePoint(ellipse.center)
        rotateVector(ellipse.majorAxisEndPoint)
        break
      }
      case EntityType.Ray: {
        const ray = entity as RayEntity
        rotatePoint(ray.startPoint)
        rotateVector(ray.unitVector)
        break
      }
      case EntityType.Spline: {
        const spline = entity as SplineEntity
        spline.controlPoints.forEach((p) => rotatePoint(p))
        spline.fitPoints?.forEach((p) => rotatePoint(p))
        break
      }
      case EntityType.MText:
        rotatePoint((entity as MTextEntity).position)
        break
      case EntityType.AlignDimension: {
        const dim = entity as AlignDimensionEntity
        ;[dim.p1, dim.p2, dim.offsetPoint, dim.textPosition].forEach((p) => rotatePoint(p))
        break
      }
      case EntityType.MLeader: {
        const mLeader = entity as MLeaderEntity
        EntityEditHandler.forEachUniqueMLeaderPoint(mLeader, rotatePoint)
        mLeader.textRotation = (mLeader.textRotation ?? 0) + angle
        break
      }
      case EntityType.AngularDimension: {
        const angularDimension = entity as AngularDimensionEntity
        ;[
          angularDimension.line1.startPoint,
          angularDimension.line1.endPoint,
          angularDimension.line2.startPoint,
          angularDimension.line2.endPoint,
          angularDimension.arcPoint,
          angularDimension.textPosition,
        ].forEach(rotatePoint)
        break
      }
      case EntityType.Hatch: {
        const hatch = entity as HatchEntity
        if (hatch.startPoint) {
          rotatePoint(hatch.startPoint)
        }
        if (hatch.xDirection) {
          rotateVector(hatch.xDirection)
        }
        hatch.patternAngle += angle
        EntityEditHandler.forEachHatchBoundaryEntity(hatch, (boundaryEntity) => {
          EntityEditHandler.rotateEntity(boundaryEntity, params)
        })
        break
      }
      default:
        log.warn(`Rotate operation not implemented for entity type: ${entity.type}`)
    }
  }

  private static forEachHatchBoundaryEntity(
    hatch: HatchEntity,
    callback: (entity: IEntity) => void
  ) {
    ;[hatch.loops, hatch.holes].forEach((boundaryMap) => {
      Object.values(boundaryMap || {}).forEach((entities) => {
        entities.forEach((boundaryEntity) => callback(boundaryEntity))
      })
    })
  }

  private static forEachUniqueMLeaderPoint(
    mLeader: MLeaderEntity,
    callback: (point: IPoint) => void
  ) {
    const visited = new Set<IPoint>()
    const visitPoint = (point?: IPoint) => {
      if (!point || visited.has(point)) {
        return
      }
      visited.add(point)
      callback(point)
    }

    mLeader.branches.forEach((branch) => {
      visitPoint(branch.arrowPoint)
      branch.vertices.forEach(visitPoint)
    })
    visitPoint(mLeader.textPosition)
  }

  /**
   * 根据类型执行合并逻辑
   * 查找所有首尾相连的实体链并分别合并
   */
  private static joinByType(
    type: EntityType,
    entities: IEntity[],
    tolerance: number
  ): { nonJoined: IEntity[]; created: IEntity[] } {
    const result = {
      nonJoined: [] as IEntity[],
      created: [] as IEntity[],
    }

    // 根据类型获取合并函数和端点获取函数
    let joinFunc: ((entities: any[], tolerance: number) => any | null) | null = null
    let getEndPointsFunc: ((entity: any) => { start: IPoint; end: IPoint }) | null = null

    switch (type) {
      case EntityType.Polyline:
        joinFunc = EntityEditHandler.joinPolylines.bind(EntityEditHandler)
        getEndPointsFunc = EntityEditHandler.getPolylineEndPoints.bind(EntityEditHandler)
        break
      case EntityType.Spline:
        joinFunc = EntityEditHandler.joinSplines.bind(EntityEditHandler)
        getEndPointsFunc = EntityEditHandler.getSplineEndPoints.bind(EntityEditHandler)
        break
      case EntityType.Arc:
        joinFunc = EntityEditHandler.joinArcs.bind(EntityEditHandler)
        getEndPointsFunc = EntityEditHandler.getArcEndPoints.bind(EntityEditHandler)
        break
      default:
        result.nonJoined.push(...entities)
        return result
    }

    // 查找所有连接链
    const chains = EntityEditHandler.findConnectedChains(entities, getEndPointsFunc!, tolerance)

    // 对每个链进行合并
    for (const chain of chains) {
      if (chain.length < 2) {
        // 单个实体，无法合并
        result.nonJoined.push(...chain)
      } else {
        // 尝试合并
        const joined = joinFunc(chain, tolerance)
        if (joined) {
          result.created.push(joined)
        } else {
          // 合并失败，保留原实体
          result.nonJoined.push(...chain)
        }
      }
    }

    return result
  }

  /**
   * 查找所有首尾相连的实体链
   * 返回多个独立的连接链
   */
  private static findConnectedChains<T>(
    entities: T[],
    getEndPoints: (entity: T) => { start: IPoint; end: IPoint },
    tolerance: number
  ): T[][] {
    if (entities.length === 0) return []

    const chains: T[][] = []
    const remaining = [...entities]

    while (remaining.length > 0) {
      // 从第一个剩余实体开始构建链
      const chain: T[] = []
      const current = remaining.shift()!
      chain.push(current)
      let currentEnd = getEndPoints(current).end
      let currentStart = getEndPoints(current).start
      let changed = true

      // 不断向两端延伸链
      while (changed && remaining.length > 0) {
        changed = false

        // 尝试从末端连接
        for (let i = 0; i < remaining.length; i++) {
          const candidate = remaining[i]
          const { start, end } = getEndPoints(candidate)

          if (EntityEditHandler.pointsNear(currentEnd, start, tolerance)) {
            // 正向连接到末端
            chain.push(candidate)
            currentEnd = end
            remaining.splice(i, 1)
            changed = true
            break
          } else if (EntityEditHandler.pointsNear(currentEnd, end, tolerance)) {
            // 反向连接到末端
            const reversed = EntityEditHandler.reverseEntity(candidate)
            if (reversed) {
              chain.push(reversed)
              currentEnd = getEndPoints(reversed).end
              remaining.splice(i, 1)
              changed = true
              break
            }
          } else if (EntityEditHandler.pointsNear(currentStart, end, tolerance)) {
            // 正向连接到起始端
            chain.unshift(candidate)
            currentStart = start
            remaining.splice(i, 1)
            changed = true
            break
          } else if (EntityEditHandler.pointsNear(currentStart, start, tolerance)) {
            // 反向连接到起始端
            const reversed = EntityEditHandler.reverseEntity(candidate)
            if (reversed) {
              chain.unshift(reversed)
              currentStart = getEndPoints(reversed).start
              remaining.splice(i, 1)
              changed = true
              break
            }
          }
        }
      }

      chains.push(chain)
    }

    return chains
  }

  // 合并多段线
  private static joinPolylines(
    polylines: PolylineEntity[],
    tolerance: number
  ): PolylineEntity | null {
    if (polylines.length === 0) return null
    // 按顺序连接多段线
    const orderedPolylines = EntityEditHandler.orderConnectedEntities(
      polylines,
      (pl) => EntityEditHandler.getPolylineEndPoints(pl),
      tolerance
    )

    if (!orderedPolylines) {
      return null
    }

    // 合并所有顶点
    const mergedVertices: IPolylineVertice[] = []

    for (let i = 0; i < orderedPolylines.length; i++) {
      const polyline = orderedPolylines[i]
      const vertices = [...polyline.vertices]

      if (i === 0) {
        // 第一条多段线：添加所有顶点
        mergedVertices.push(...vertices)
      } else {
        // 后续多段线：跳过第一个顶点（与前一条的最后一个顶点重合）
        // 但需要把该“重合点”上的 bulge 转移到 merged 的最后一个顶点上：
        // bulge 存在顶点上，表示从该顶点到下一个顶点的圆弧段。
        // 合并后该段应由 merged 的最后一个点指向新加入的下一个点。
        if (mergedVertices.length > 0 && vertices.length > 0) {
          mergedVertices[mergedVertices.length - 1].bulge = vertices[0].bulge || 0
        }
        mergedVertices.push(...vertices.slice(1))
      }
    }

    // 检查是否形成闭合
    const first = mergedVertices[0]
    const last = mergedVertices[mergedVertices.length - 1]
    const isClosed = EntityEditHandler.pointsNear(first, last, tolerance)

    if (isClosed) {
      // 移除最后一个重复点
      mergedVertices.pop()
    }

    // 创建合并后的多段线
    return new PolylineEntity({
      id: nanoid(),
      layerId: polylines[0].layerId,
      vertices: mergedVertices,
      isClosed,
      color: polylines[0].color,
      lineWidth: polylines[0].lineWidth,
      lineType: polylines[0].lineType,
    })
  }

  /**
   * 合并样条曲线
   */
  private static joinSplines(splines: SplineEntity[], tolerance: number): SplineEntity | null {
    if (splines.length === 0) return null

    // 检查所有样条是否使用相同的创建方法
    const firstMethod = splines[0].method
    const allSameMethod = splines.every((s) => s.method === firstMethod)

    if (!allSameMethod) {
      return null
    }

    // 按顺序连接样条
    const orderedSplines = EntityEditHandler.orderConnectedEntities(
      splines,
      (sp) => EntityEditHandler.getSplineEndPoints(sp),
      tolerance
    )

    if (!orderedSplines) {
      return null
    }

    if (firstMethod === 'FIT') {
      // 拟合点模式：合并拟合点
      const mergedFitPoints: IPoint[] = []

      for (let i = 0; i < orderedSplines.length; i++) {
        const spline = orderedSplines[i]
        const fitPoints = spline.fitPoints || []

        if (i === 0) {
          mergedFitPoints.push(...fitPoints)
        } else {
          // 跳过第一个点（与前一条的最后一个点重合）
          mergedFitPoints.push(...fitPoints.slice(1))
        }
      }

      // 检查是否闭合
      const first = mergedFitPoints[0]
      const last = mergedFitPoints[mergedFitPoints.length - 1]
      const isClosed = EntityEditHandler.pointsNear(first, last, tolerance)

      if (isClosed) {
        mergedFitPoints.pop()
      }

      // 重新计算控制点和节点向量
      const { controlPoints, knots, degree } = SplineUtils.fitPointsToSpline(
        mergedFitPoints,
        splines[0].degree,
        orderedSplines[0].startTangent,
        orderedSplines[orderedSplines.length - 1].endTangent,
        isClosed
      )

      return new SplineEntity({
        id: nanoid(),
        layerId: splines[0].layerId,
        method: 'FIT',
        degree,
        fitPoints: mergedFitPoints,
        controlPoints,
        knots,
        isClosed,
        startTangent: orderedSplines[0].startTangent,
        endTangent: orderedSplines[orderedSplines.length - 1].endTangent,
        color: splines[0].color,
        lineWidth: splines[0].lineWidth,
        lineType: splines[0].lineType,
      })
    } else {
      // 控制点模式：合并控制点
      const mergedControlPoints: IPoint[] = []

      for (let i = 0; i < orderedSplines.length; i++) {
        const spline = orderedSplines[i]
        const controlPoints = spline.controlPoints || []

        if (i === 0) {
          mergedControlPoints.push(...controlPoints)
        } else {
          mergedControlPoints.push(...controlPoints.slice(1))
        }
      }

      const first = mergedControlPoints[0]
      const last = mergedControlPoints[mergedControlPoints.length - 1]
      const isClosed = EntityEditHandler.pointsNear(first, last, tolerance)

      if (isClosed) {
        mergedControlPoints.pop()
      }

      // 生成新的节点向量
      const knots = SplineUtils.generateKnots(
        mergedControlPoints.length,
        splines[0].degree,
        isClosed
      )

      return new SplineEntity({
        id: nanoid(),
        layerId: splines[0].layerId,
        method: 'CV',
        degree: splines[0].degree,
        controlPoints: mergedControlPoints,
        knots,
        isClosed,
        color: splines[0].color,
        lineWidth: splines[0].lineWidth,
        lineType: splines[0].lineType,
      })
    }
  }

  /**
   * 合并圆弧
   * 将首尾相连的圆弧合并
   */
  private static joinArcs(
    arcs: ArcEntity[],
    tolerance: number
  ): PolylineEntity | CircleEntity | null {
    if (arcs.length === 0) return null

    const originArc = arcs[0]

    // 检查所有圆弧是否同心同半径
    // const sameCircle = arcs.every(arc => {
    //     const centerDist = Math.sqrt(
    //         Math.pow(arc.center.x - originArc.center.x, 2) +
    //         Math.pow(arc.center.y - originArc.center.y, 2)
    //     );
    //     const radiusDiff = Math.abs(arc.radius - originArc.radius);
    //     return centerDist <= tolerance && radiusDiff <= tolerance;
    // });
    // if (!sameCircle) {
    //     return null;
    // }

    // 按顺序连接圆弧
    const orderedArcs = EntityEditHandler.orderConnectedEntities(
      arcs,
      (arc) => EntityEditHandler.getArcEndPoints(arc),
      tolerance
    )

    if (!orderedArcs) {
      return null
    }

    // 检查合并后的圆弧是否形成完整圆或大于 360°
    let totalAngle = 0
    for (const arc of orderedArcs) {
      let arcAngle = arc.endAngle - arc.startAngle

      // 处理角度跨越问题
      if (arc.clockwise) {
        if (arcAngle > 0) arcAngle -= 2 * Math.PI
      } else {
        if (arcAngle < 0) arcAngle += 2 * Math.PI
      }

      totalAngle += Math.abs(arcAngle)
    }

    // 如果形成完整圆，转换为圆
    if (Math.abs(totalAngle - 2 * Math.PI) < 0.01) {
      return new CircleEntity({
        id: nanoid(),
        layerId: originArc.layerId,
        center: { x: originArc.center.x, y: originArc.center.y },
        radius: originArc.radius,
        color: originArc.color,
        lineWidth: originArc.lineWidth,
        lineType: originArc.lineType,
      })
    }

    // 将连接的圆弧转换为多段线（使用 bulge 参数表示每段圆弧），保留每段的端点
    // bulge 存放在顶点，用以表示从该顶点到下一个顶点的圆弧段
    const computeSignedSweep = (arc: ArcEntity) => {
      let delta = arc.endAngle - arc.startAngle
      // normalize to (-2PI, 2PI)
      while (delta <= -2 * Math.PI) delta += 2 * Math.PI
      while (delta > 2 * Math.PI) delta -= 2 * Math.PI

      if (arc.clockwise) {
        if (delta > 0) delta -= 2 * Math.PI
      } else {
        if (delta < 0) delta += 2 * Math.PI
      }
      return delta
    }

    const bulges: number[] = orderedArcs.map((a) => {
      const angle = computeSignedSweep(a)
      return Math.tan(angle / 4)
    })

    const vertices: IPolylineVertice[] = []
    // 首个顶点为第一个圆弧的起点，包含第一个 bulge
    const firstStart = EntityEditHandler.getArcEndPoints(orderedArcs[0]).start
    vertices.push({ x: firstStart.x, y: firstStart.y, bulge: bulges[0] })

    // 对于每个圆弧，添加其终点；该终点的 bulge 应为下一段圆弧的 bulge（若存在），否则为 0
    for (let i = 0; i < orderedArcs.length; i++) {
      const endPt = EntityEditHandler.getArcEndPoints(orderedArcs[i]).end
      const nextBulge = i + 1 < bulges.length ? bulges[i + 1] : 0
      vertices.push({ x: endPt.x, y: endPt.y, bulge: nextBulge })
    }

    // 检查是否闭合（首尾重合）并移除重复的末点
    const isClosed = EntityEditHandler.pointsNear(
      vertices[0],
      vertices[vertices.length - 1],
      tolerance
    )
    if (isClosed) {
      vertices.pop()
    }

    return new PolylineEntity({
      id: nanoid(),
      layerId: originArc.layerId,
      vertices,
      isClosed,
      color: originArc.color,
      lineWidth: originArc.lineWidth,
      lineType: originArc.lineType,
    })
  }

  /**
   * 将实体按首尾相连的顺序排列
   */
  private static orderConnectedEntities<T>(
    entities: T[],
    getEndPoints: (entity: T) => { start: IPoint; end: IPoint; reversed?: boolean },
    tolerance: number
  ): T[] | null {
    if (entities.length === 0) return null
    if (entities.length === 1) return entities

    const ordered: T[] = []
    const remaining = [...entities]

    // 从第一个实体开始
    const current = remaining.shift()!
    ordered.push(current)
    let currentEnd = getEndPoints(current).end

    while (remaining.length > 0) {
      let found = false

      for (let i = 0; i < remaining.length; i++) {
        const candidate = remaining[i]
        const { start, end } = getEndPoints(candidate)

        // 检查是否可以连接（正向或反向）
        if (EntityEditHandler.pointsNear(currentEnd, start, tolerance)) {
          // 正向连接
          ordered.push(candidate)
          currentEnd = end
          remaining.splice(i, 1)
          found = true
          break
        } else if (EntityEditHandler.pointsNear(currentEnd, end, tolerance)) {
          // 反向连接 - 需要翻转实体
          const reversed = EntityEditHandler.reverseEntity(candidate)
          if (reversed) {
            ordered.push(reversed)
            currentEnd = getEndPoints(reversed).end
            remaining.splice(i, 1)
            found = true
            break
          }
        }
      }

      if (!found) {
        // 无法找到连接的实体
        return null
      }
    }

    return ordered
  }

  /**
   * 翻转实体（反转顶点/控制点顺序）
   */
  private static reverseEntity<T>(entity: T): T | null {
    if (entity instanceof PolylineEntity) {
      const reversed = entity.clone() as PolylineEntity
      // Reverse vertices order while keeping bulge semantics.
      // In this codebase, bulge is stored on a vertex and applies to the segment from that vertex to the NEXT vertex.
      // When reversing direction, each segment reverses direction, so bulge changes sign.
      // Additionally, because bulge is attached to the segment start vertex, reversing requires shifting bulges.
      const original = [...reversed.vertices]
      const n = original.length
      const out: IPolylineVertice[] = new Array(n)

      for (let i = 0; i < n; i++) {
        const origIndex = n - 1 - i
        out[i] = { ...original[origIndex] }
      }

      if (n > 0) {
        if (reversed.isClosed) {
          for (let i = 0; i < n; i++) {
            const origIndex = n - 1 - i
            const prevOrigIndex = (origIndex - 1 + n) % n
            out[i].bulge = -(original[prevOrigIndex].bulge || 0)
          }
        } else {
          for (let i = 0; i < n - 1; i++) {
            const origIndex = n - 1 - i
            out[i].bulge = -(original[origIndex - 1].bulge || 0)
          }
          // Last vertex has no outgoing segment in open polyline
          out[n - 1].bulge = 0
        }
      }

      reversed.vertices = out
      return reversed as T
    } else if (entity instanceof SplineEntity) {
      const reversed = entity.clone() as SplineEntity
      if (reversed.fitPoints) {
        reversed.fitPoints = [...reversed.fitPoints].reverse()
      }
      reversed.controlPoints = [...reversed.controlPoints].reverse()
      // 交换起始和结束切线
      ;[reversed.startTangent, reversed.endTangent] = [reversed.endTangent, reversed.startTangent]
      return reversed as T
    } else if (entity instanceof ArcEntity) {
      const reversed = entity.clone() as ArcEntity
      // 交换起始和结束角度
      ;[reversed.startAngle, reversed.endAngle] = [reversed.endAngle, reversed.startAngle]
      // 反转方向
      reversed.clockwise = !reversed.clockwise
      return reversed as T
    }
    return null
  }

  /**
   * 获取多段线的端点
   */
  private static getPolylineEndPoints(polyline: PolylineEntity): { start: IPoint; end: IPoint } {
    const vertices = polyline.vertices
    return {
      start: { x: vertices[0].x, y: vertices[0].y },
      end: { x: vertices[vertices.length - 1].x, y: vertices[vertices.length - 1].y },
    }
  }

  /**
   * 获取样条曲线的端点
   */
  private static getSplineEndPoints(spline: SplineEntity): { start: IPoint; end: IPoint } {
    const points = spline.method === 'FIT' ? spline.fitPoints : spline.controlPoints
    if (!points || points.length === 0) {
      return { start: { x: 0, y: 0 }, end: { x: 0, y: 0 } }
    }
    return {
      start: { x: points[0].x, y: points[0].y },
      end: { x: points[points.length - 1].x, y: points[points.length - 1].y },
    }
  }

  /**
   * 获取圆弧的端点
   */
  private static getArcEndPoints(arc: ArcEntity): { start: IPoint; end: IPoint } {
    const startX = arc.center.x + arc.radius * Math.cos(arc.startAngle)
    const startY = arc.center.y + arc.radius * Math.sin(arc.startAngle)
    const endX = arc.center.x + arc.radius * Math.cos(arc.endAngle)
    const endY = arc.center.y + arc.radius * Math.sin(arc.endAngle)

    return {
      start: { x: startX, y: startY },
      end: { x: endX, y: endY },
    }
  }

  /**
   * 判断两个点是否接近（在容差范围内）
   */
  private static pointsNear(p1: IPoint, p2: IPoint, tolerance: number): boolean {
    const dx = p1.x - p2.x
    const dy = p1.y - p2.y
    const distance = Math.sqrt(dx * dx + dy * dy)
    return distance <= tolerance
  }
}
